/**
 * Web component for embedding child Anear apps
 *
 * Usage:
 *   <anear-app name="app-slug"></anear-app>
 *   <anear-app name="app-slug" options='{"key": "value"}'></anear-app>
 *
 * The component creates a child event, joins as participant, and renders
 * the child app in a shadow DOM for CSS isolation.
 *
 * Styling and positioning of the child app is controlled by the parent app's
 * CSS styling of this <anear-app> element. The child app's own CSS handles
 * its internal layout and any widget/popup states it supports.
 *
 * The `options` attribute accepts a JSON string that will be parsed and passed
 * as `initial_context` to the child app's state machine. This allows parent apps
 * to configure the initial context of child apps (e.g., widget state, titles, etc.)
 */
import React from 'react'
import { createRoot } from 'react-dom/client'
import anearApi from '../Api/AnearApi'
import ChildEventFrame from '../Pages/ChildEventFrame'
import CssLoader from '../Utils/CssLoader'
import userMessaging from '../Utils/UserMessaging'
import UserAuthContainer from '../Containers/UserAuthContainer'
import * as logger from 'loglevel'

class AnearApp extends HTMLElement {
  constructor() {
    super()
    this.attachShadow({ mode: 'open' })
    this._appSlug = null
    this._initialContext = null
    this._childEvent = null
    this._childParticipant = null
    this._parentEvent = null
    this._parentParticipant = null
    this._initialized = false
    this._container = null
    this._reactRoot = null
    this._teardownScheduled = false
  }

  static get observedAttributes() {
    return ['name', 'options']
  }

  /**
   * Tear down the Shadow DOM + React tree so no UI artifacts remain.
   *
   * Notes:
   * - This is intentionally separate from lifecycle/messaging teardown.
   * - Used for terminal parent exits (CLOSED/CANCELED/BOOTED/EVENT_UNMOUNT).
   * - Does NOT require a full page refresh to hide the widget; it removes the DOM content directly.
   */
  teardownUI(reason = null) {
    try {
      // Unmount React first to avoid leaks
      if (this._reactRoot) {
        try {
          this._reactRoot.unmount()
        } catch (_e) {
          // ignore
        }
        this._reactRoot = null
      }

      // Clear shadow DOM
      if (this.shadowRoot) {
        while (this.shadowRoot.firstChild) {
          this.shadowRoot.removeChild(this.shadowRoot.firstChild)
        }
      }

      // Drop references
      this._container = null

      // Hide the host element (prevents "ghost bubble" if parent remains mounted)
      this.style.display = 'none'
      if (reason) this.setAttribute('data-teardown-reason', String(reason))
    } catch (err) {
      logger.error('AnearApp: Error during teardownUI', err)
    }
  }

  connectedCallback() {
    // If this element was previously torn down, ensure it can render again.
    // `teardownUI()` intentionally hides the host element; reconnect/reuse must unhide it.
    this.style.display = ''
    this.removeAttribute('data-teardown-reason')

    const appSlug = this.getAttribute('name')
    const optionsStr = this.getAttribute('options')

    // Parse options if provided
    let initialContext = null
    if (optionsStr) {
      try {
        initialContext = JSON.parse(optionsStr)
        if (!initialContext || typeof initialContext !== 'object' || Array.isArray(initialContext)) {
          logger.warn('AnearApp: options must be a JSON object, ignoring invalid value')
          initialContext = null
        }
      } catch (err) {
        logger.error('AnearApp: Invalid JSON in options attribute', err)
        initialContext = null
      }
    }

    if (appSlug && !this._initialized) {
      this._appSlug = appSlug
      this._initialContext = initialContext
      // Initialize will be called by EventFrame with parent context
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'name' && newValue !== oldValue) {
      this._appSlug = newValue
      if (this._initialized) {
        // Re-initialize if name changes
        this.initialize(this._parentEvent, this._parentParticipant)
      }
    } else if (name === 'options' && newValue !== oldValue) {
      // Parse new options value
      let initialContext = null
      if (newValue) {
        try {
          initialContext = JSON.parse(newValue)
          if (!initialContext || typeof initialContext !== 'object' || Array.isArray(initialContext)) {
            logger.warn('AnearApp: options must be a JSON object, ignoring invalid value')
            initialContext = null
          }
        } catch (err) {
          logger.error('AnearApp: Invalid JSON in options attribute', err)
          initialContext = null
        }
      }
      this._initialContext = initialContext
      if (this._initialized) {
        // Re-initialize if options change
        this.initialize(this._parentEvent, this._parentParticipant)
      }
    }
  }

  /**
   * Initialize the component with parent event and participant
   * Called by EventFrame to provide parent context
   * IMPORTANT: Parent participant relationship must be fully established before calling this
   */
  async initialize(parentEvent, parentParticipant, userAuthContainer = null) {
    if (!this._appSlug) {
      logger.warn('AnearApp: name attribute is required')
      return
    }

    // Check if parent participant has changed (reconnect scenario)
    const parentParticipantChanged = this._parentParticipant !== parentParticipant &&
                                     this._parentParticipant !== null &&
                                     parentParticipant !== null

    // Check if we already have a child event (either from previous initialization or stored ID)
    const storedChildEventId = this.getAttribute('data-child-event-id')

    if (this._initialized && this._childEvent) {
      // Already initialized with a child event
      if (storedChildEventId && this._childEvent.id === storedChildEventId) {
        // Same child event - check if we need to update due to parent participant change
        if (parentParticipantChanged) {
          // Parent participant changed (reconnect scenario) - update reference
          logger.debug(`AnearApp: Parent participant changed, updating child event ${this._childEvent.id} connection`)
          this._parentParticipant = parentParticipant
          // Child participant should already exist and will reconnect via presence updates
          // But we should ensure child participant is still valid
          if (this._childParticipant) {
            // Re-initialize messaging to ensure channels are set up correctly
            await this._childParticipant.initMessaging()
          }
          return
        }
        // Same child event, same participant - skip re-initialization
        logger.debug(`AnearApp: Already initialized with child event ${this._childEvent.id}`)
        return
      }
    }

    // If we have a stored child event ID but no child event object, we could try to reload it
    // For now, we'll let setupChildApp handle the find-or-create logic

    this._parentEvent = parentEvent
    this._parentParticipant = parentParticipant
    this._userAuthContainer = userAuthContainer
    this._initialized = true

    await this.setupChildApp()
  }

  async setupChildApp() {
    try {
      // Find or create child event
      // First, check if a child event already exists for this parent event + app slug
      const existingChildEvents = await anearApi.getChildEvents(this._parentEvent.id)

      // Find existing child event for this app slug
      // Match by checking the zone.app.attributes.slug from the included data
      let existingChildEvent = null
      for (const childEvent of existingChildEvents) {
        // Access app slug from the zone relationship
        if (childEvent.zone && childEvent.zone.app) {
          // AnearApp extends JsonApiResource, so attributes are accessible via .attributes
          const childAppSlug = childEvent.zone.app.attributes?.slug
          if (childAppSlug === this._appSlug) {
            // Found existing child event for this app slug
            existingChildEvent = childEvent
            logger.debug(`AnearApp: Found existing child event ${childEvent.id} for app ${this._appSlug}`)
            break
          }
        }
      }

      if (existingChildEvent) {
        // Reuse existing child event (rejoining scenario)
        logger.debug(`AnearApp: Reusing existing child event ${existingChildEvent.id} for app ${this._appSlug}`)
        this._childEvent = existingChildEvent

        // Re-initialize messaging for the existing child event (rejoin scenario)
        await this._childEvent.initMessaging()
      } else {
        // Create new child event
        logger.debug(`AnearApp: Creating new child event for app ${this._appSlug}`)
        this._childEvent = await anearApi.createChildEvent(
          this._appSlug,
          this._parentEvent.id,
          this._parentEvent.name,
          this._parentEvent.requiresGeoLocationToParticipate(),
          this._initialContext
        )

        // Initialize messaging for the new child event
        await this._childEvent.initMessaging()
      }

      // Store child event ID in data attribute for persistence across re-renders
      this.setAttribute('data-child-event-id', this._childEvent.id)

      // Register eventId -> appSlug for log attribution (used by UserMessaging prefixes)
      try {
        // Note: Ably channel names use compact_id(event.id), not UUID. Register both.
        userMessaging.registerEventAppSlug(this._childEvent.id, this._appSlug)
        userMessaging.registerEventAppSlugForChannel(this._childEvent.attributes?.['event-channel-name'], this._appSlug)
      } catch (_e) {
        // ignore
      }

      // Get child app info to check inherit_participant_role and spectators_allowed flags
      const childApp = await anearApi.getAppBySlug(this._appSlug)
      if (!childApp) {
        throw new Error(`Child app '${this._appSlug}' not found`)
      }

      const appAttributes = childApp.app.attributes
      const embeddable =
        appAttributes['embeddable'] ??
        appAttributes.embeddable ??
        false

      if (embeddable !== true) {
        throw new Error(
          `App '${this._appSlug}' is not embeddable. Refusing to mount as a child app. ` +
          `Set app.flags.embeddable=true on the App record (ANAPI) to allow <anear-app name="${this._appSlug}">.`
        )
      }
      const inheritRole = appAttributes['inherit-participant-role'] ??
                         appAttributes.inherit_participant_role ??
                         true // Default to true for backward compatibility
      // Note: spectators_allowed is checked via this._childEvent.spectatorsAllowed() 
      // which reflects the app's flag configuration

      // Check if we already have a participant record in this child event
      // IMPORTANT: Even if isCreator() is true, we still need to join as participant
      // because being the creator doesn't automatically create a participant record.
      // The participant record is only created when we explicitly join via becomeEventParticipant().
      const isParticipantInChildEvent = this._childEvent.isParticipant()

      if (isParticipantInChildEvent) {
        // Event indicates we're already a participant (rejoining scenario)
        try {
          this._childParticipant = await anearApi.getEventParticipant(this._childEvent.id)
          if (this._childParticipant) {
            // Re-initialize messaging for the existing participant (rejoin scenario)
            await this._childParticipant.initMessaging()
            logger.debug(`AnearApp: Rejoined as participant in child event ${this._childEvent.id}`)
          }
        } catch (err) {
          // Event says we're a participant but we can't get participant object
          // This might happen if the event state is inconsistent - fall through to join logic
          logger.warn(`AnearApp: Event indicates participant but getEventParticipant failed: ${err.message}`)
          // Fall through to join logic below
        }
      }

      // Join as participant/spectator if we don't have a participant yet
      // IMPORTANT: Parent participant relationship must be fully established before this runs
      // (ensured by EventFrame waiting for parent participant before calling initialize)
      // Also note: Even creators need to explicitly join - isCreator() doesn't create a participant record
      if (!this._childParticipant) {
        // Determine join strategy based on parent role and child app policy
        const parentIsCreator = this._parentEvent.isCreator()
        const parentIsParticipant = this._parentParticipant !== null
        
        // Check child app flags (from App record, not inherited from parent)
        // Child apps should have compatible flags configured on their App record
        const childIsOpenHouse = this._childEvent.isOpenHouse() || 
                                 this._childEvent.spectatorsAllowed()

        // Join as participant if ANY of these conditions are true:
        // 1. Parent is creator (creator should always join as participant in child, even in created state)
        //    - Even though isCreator() returns true, we still need to call becomeEventParticipant()
        //    - This creates the participant record and allows entering presence
        // 2. Parent is participant AND child inherits role (inherit_participant_role = true)
        // 3. Child is open_house (anyone can join as participant in open_house events)
        const shouldJoinAsParticipant = parentIsCreator ||
                                        (parentIsParticipant && inheritRole) ||
                                        childIsOpenHouse

        // Otherwise, try spectator if:
        // - Child allows spectators AND
        // - Either: (parent is spectator AND inherits role) OR (child doesn't inherit role)
        const shouldJoinAsSpectator = !shouldJoinAsParticipant && 
                                      this._childEvent.spectatorsAllowed() &&
                                      ((!parentIsParticipant && inheritRole) || !inheritRole)

        if (shouldJoinAsParticipant) {
          // Join as participant - this creates the participant record
          // For creators joining a newly created child event, this is the first time we're creating the participant
          this._childParticipant = await anearApi.becomeEventParticipant(this._childEvent)
          // Initialize messaging for the new participant (sets up channels)
          await this._childParticipant.initMessaging()
          logger.debug(`AnearApp: Joined as participant in child event ${this._childEvent.id} (creator=${parentIsCreator}, parentParticipant=${parentIsParticipant}, openHouse=${childIsOpenHouse}, inheritRole=${inheritRole})`)
          // NOTE: The child participant will enter presence in ChildEventFrame.setupParticipant()
          // which happens after renderChildApp() is called
        } else if (shouldJoinAsSpectator) {
          // Join as spectator (parent is spectator, child inherits, and child allows spectators)
          // OR (child allows self-declaration and spectators)
          // For spectators, we don't create a participant object, but we'll subscribe to spectator channel
          // ChildEventFrame will handle spectator subscriptions
          this._childEvent.initSpectatorMessaging()
          this._childParticipant = null
          logger.debug(`AnearApp: Viewing child event ${this._childEvent.id} as spectator`)
        } else {
          // Edge case: Can't join as participant or spectator
          // This shouldn't happen for child events if flags are configured correctly
          logger.warn(`AnearApp: Cannot join child event ${this._childEvent.id} - no valid role (parentIsCreator=${parentIsCreator}, parentIsParticipant=${parentIsParticipant}, inheritRole=${inheritRole}, spectatorsAllowed=${this._childEvent.spectatorsAllowed()}, childIsOpenHouse=${childIsOpenHouse})`)
          // Still render the child app - it might handle this case or show an error
          this._childParticipant = null
        }
      }

      // Render child app in shadow DOM
      this.renderChildApp()

      // Register for presence propagation (handled by EventFrame)
    } catch (err) {
      logger.error('AnearApp: Error setting up child app', err)
      this.showError(err.message)
    }
  }

  renderChildApp() {
    if (!this._childEvent) {
      logger.warn('AnearApp: Cannot render without child event')
      return
    }

    // Ensure host element is visible (it may have been hidden by teardownUI()).
    this.style.display = ''

    // Clear any existing content (CSS will be re-added after clearing)
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild)
    }

    // Inject CSS into shadow root AFTER clearing
    // This ensures the CSS link is not removed, and ensureAppCssInShadow
    // will handle checking for duplicates if called multiple times
    CssLoader.ensureAppCssInShadow(this._childEvent, this.shadowRoot)

    // Create a container div in shadow DOM.
    // Child apps style this generic container. The parent positions the
    // <anear-app> host. Size to content so a floating widget does not collapse.
    const container = document.createElement('div')
    container.id = `anear-app-container-${this._childEvent.id}`
    container.className = 'anear-child-app'
    container.style.width = 'auto'
    container.style.height = 'auto'
    container.style.minWidth = '1px'
    container.style.minHeight = '1px'
    container.style.position = 'relative'
    // Don't clip overflow - allow fixed-positioned children to escape the widget.
    container.style.overflow = 'visible'
    // Ensure full opacity - Shadow DOM container must be fully opaque
    container.style.opacity = '1'
    this.shadowRoot.appendChild(container)
    this._container = container
    
    // Also ensure the Shadow DOM host element itself has explicit styling
    // This prevents any parent styles from affecting the Shadow DOM content
    this.style.opacity = '1'
    this.style.isolation = 'isolate'

    // Render ChildEventFrame - child app handles its own state/view rendering
    // Note: anearParticipant may be null if user joined as spectator
    // Wrap with UserAuthContainer.Provider since we're rendering outside the main React tree
    // Each Provider will have its own state, but it's backed by the same localStorage/API
    // Use createRoot for React 18 compatibility
    if (this._reactRoot) {
      // Unmount previous root if it exists
      this._reactRoot.unmount()
    }
    this._reactRoot = createRoot(container)
    // Pass the parent's userAuth state as initialState to prevent reloading user
    // which would close and reinitialize the Ably Realtime singleton
    const initialState = this._userAuthContainer ? this._userAuthContainer.state : undefined
    this._reactRoot.render(
      React.createElement(
        UserAuthContainer.Provider,
        { initialState },
      React.createElement(ChildEventFrame, {
        anearEvent: this._childEvent,
        anearParticipant: this._childParticipant
        })
      )
    )
  }

  showError(message) {
    const errorDiv = document.createElement('div')
    errorDiv.style.padding = '1rem'
    errorDiv.style.color = 'red'
    errorDiv.textContent = `Error loading child app: ${message}`
    this.shadowRoot.appendChild(errorDiv)
  }

  /**
   * Propagate boot to child event participant
   * Called when parent participant is booted
   */
  async propagateBoot(reason) {
    if (!this._childParticipant) return

    try {
      logger.debug(`AnearApp: Propagating boot to child event ${this._childEvent?.id}`)
      await this._childParticipant.leaveEvent('BOOTED')
      if (this._childEvent) {
        await this._childEvent.closeMessaging()
      }
      // Remove UI artifacts after terminal boot
      this.teardownUI('BOOTED')
    } catch (err) {
      logger.error('AnearApp: Error propagating boot to child event', err)
    }
  }

  /**
   * Propagate exit to child event participant
   * Called when parent participant exits (e.g., via <anear-nav action="back">)
   */
  async propagateExit(exitType = 'EVENT_UNMOUNT') {
    if (!this._childParticipant) return
    if (this._propagateExitPromise) return this._propagateExitPromise

    try {
      logger.debug(`AnearApp: Propagating exit (${exitType}) to child event ${this._childEvent?.id}`)
      // IMPORTANT:
      // In the JS API, Ably presence leaves are treated as a DISCONNECT by default unless
      // the presence leave includes `type: 'PARTICIPANT_EXIT'` (a permanent leave).
      // For terminal parent exits, we want the child event to treat this as a permanent exit
      // so the child AppM can shut down immediately (no reconnect-grace window).
      const isTerminal =
        exitType === 'CLOSED' ||
        exitType === 'CANCELED' ||
        exitType === 'BOOTED'

      const leaveType = isTerminal ? 'PARTICIPANT_EXIT' : exitType
      this._propagateExitPromise = this._childParticipant.leaveEvent(leaveType)
      await this._propagateExitPromise

      // On terminal parent exits, tear down the UI so nothing remains visible.
      // (Non-terminal "hide" should be handled by parent omitting <anear-app>, which ABR now supports.)
      if (['CLOSED', 'CANCELED', 'BOOTED', 'EVENT_UNMOUNT'].includes(exitType)) {
        this.teardownUI(exitType)
      }
    } catch (err) {
      logger.error('AnearApp: Error propagating exit to child event', err)
    } finally {
      // Allow later exits (e.g., terminal after non-terminal) to run.
      this._propagateExitPromise = null
    }
  }

  /**
   * Propagate presence enter to child event participant
   * Called when parent participant enters presence programmatically
   */
  async propagatePresenceEnter(requiresGeoLocation = false) {
    if (!this._childParticipant) return

    try {
      logger.debug(`AnearApp: Propagating presence enter to child event ${this._childEvent?.id}`)
      await this._childParticipant.setActionPresence(requiresGeoLocation)
    } catch (err) {
      logger.error('AnearApp: Error propagating presence enter to child event', err)
    }
  }

  /**
   * Propagate presence leave to child event participant
   * Called when parent participant leaves presence programmatically
   */
  async propagatePresenceLeave(options = {}) {
    if (!this._childParticipant) return

    try {
      logger.debug(`AnearApp: Propagating presence leave to child event ${this._childEvent?.id}`, options)
      await this._childParticipant.leaveActionPresence(options)
    } catch (err) {
      logger.error('AnearApp: Error propagating presence leave to child event', err)
    }
  }

  /**
   * Propagate presence update to child event participant
   * Called when parent participant updates presence programmatically (e.g., geoLocation change)
   */
  async propagatePresenceUpdate(requiresGeoLocation = false) {
    if (!this._childParticipant) return

    try {
      logger.debug(`AnearApp: Propagating presence update to child event ${this._childEvent?.id}`)
      await this._childParticipant.updateActionPresence(requiresGeoLocation)
    } catch (err) {
      logger.error('AnearApp: Error propagating presence update to child event', err)
    }
  }

  disconnectedCallback() {
    // Cleanup when component is removed
    this.propagateExit('EVENT_UNMOUNT').catch(err => {
      logger.error('AnearApp: Error leaving child event', err)
    })

    // Always clear UI on disconnect to prevent orphaned Shadow DOM nodes.
    // Important: this custom element can be removed during a React commit.
    // Deferring avoids "Attempted to synchronously unmount a root while React was already rendering".
    if (!this._teardownScheduled) {
      this._teardownScheduled = true
      setTimeout(() => {
        this.teardownUI('DISCONNECTED')
        this._teardownScheduled = false
      }, 0)
    }
  }

  // Getters for external access
  get childEvent() {
    return this._childEvent
  }

  get childParticipant() {
    return this._childParticipant
  }
}

customElements.define('anear-app', AnearApp)

export default AnearApp
